import { refreshAccessToken } from '../config/api_config'
import clearLoginData from './clearLoginData'

// Must stay below config('custom.session.idle_timeout_minutes') (currently
// 30 min) - set higher (20 min) to cut server load, at the cost of the local
// idle-logout UI reacting up to ~10 min after the true deadline in the worst
// case. Not a security gap either way: the real deadline is enforced
// server-side (the refresh token's own expires_at), checked precisely on
// every real API call regardless of this timer.
const CHECK_INTERVAL_MS = 20 * 60 * 1000

// Must match config('custom.session.idle_timeout_minutes') on the backend
// (social-auth-service/config/custom.php) - keep these two in sync by hand
// whenever either one changes. Used to detect "gone completely idle" locally:
// if literally nothing happens (no activity at all), the heartbeat below
// never calls the server, so it would otherwise never notice the session
// already died.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000

// Deliberately excludes 'mousemove' - resting a hand on the mouse/trackpad
// fires it constantly without any real interaction, which made idle testing
// (and real idle detection) unreliable.
const ACTIVITY_EVENTS = ['keydown', 'click', 'scroll', 'touchstart']

// null means "no activity observed since the last check" - starting both
// timestamps equal would make the very first tick look like activity even
// when the user hasn't done anything yet.
let lastActivityAt = null
let lastCheckedAt = Date.now()
// The last moment the session was known good (login, or a successful
// refresh). Drives the local idle check below.
let lastConfirmedAt = Date.now()
let started = false

const markActivity = () => {
  lastActivityAt = Date.now()
}

const forceLogout = () => {
  clearLoginData()
  window.location.href = '/auth/login'
}

// Dedupe concurrent refresh calls: uses the SAME shared, module-level
// in-flight promise as api_config.js's own reactive 401 handler (not a
// separate one local to this file) - otherwise a heartbeat-triggered refresh
// and a reactive-401-triggered refresh could fire at nearly the same moment,
// each try to rotate the same single-use refresh token, and the loser gets
// rejected even though the session is still fine.
//
// alreadyIdle: true when we're calling this because our own local idle check
// already decided the session must be dead. In that case we still want the
// server told (so the DB rows actually get marked revoked instead of just
// sitting there), but must log out locally regardless of how the network
// call goes - we already know the answer, we're just informing the server.
const refreshSession = (alreadyIdle = false) => {
  return refreshAccessToken()
    .then(() => {
      lastConfirmedAt = Date.now()
    })
    .catch(error => {
      // Either the server confirmed it's expired (401) or the request failed
      // outright (offline, no refresh token, etc). Either way, if we already
      // independently decided locally that the session is idle-dead, log out
      // regardless - this was just a best-effort attempt to tell the server too.
      if (alreadyIdle || (error.response && error.response.status === 401)) {
        forceLogout()
      }
    })
}

const tick = () => {
  const hasSession = !!localStorage.getItem('access_token')
  if (!hasSession) {
    return
  }

  const hadActivity = lastActivityAt !== null && lastActivityAt >= lastCheckedAt
  lastCheckedAt = Date.now()

  if (hadActivity) {
    lastActivityAt = null
    refreshSession()
    return
  }

  // No activity this cycle - nothing was sent to the server, so nothing
  // renewed the deadline. If we've gone past the idle window since the last
  // confirmed-good moment, the refresh token has already died: tell the
  // server (so its DB rows get marked revoked) and log out locally either way.
  if (Date.now() - lastConfirmedAt > IDLE_TIMEOUT_MS) {
    refreshSession(true)
  }
}

export const startSessionHeartbeat = () => {
  if (started) {
    return
  }
  started = true

  ACTIVITY_EVENTS.forEach(event => {
    window.addEventListener(event, markActivity, { passive: true })
  })

  setInterval(tick, CHECK_INTERVAL_MS)
}
